
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { supabase } from "@/integrations/supabase/client";
import { generateSlug } from "@/lib/utils";
import { Album } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Album name must be at least 2 characters.",
  }),
  description: z.string().optional(),
  isPublic: z.boolean().default(false),
});

interface AlbumCreatorProps {
  onSuccess?: (album: Album) => void;
}

const AlbumCreator = ({ onSuccess }: AlbumCreatorProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { session } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      isPublic: false,
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!session?.user) {
      toast.error("You must be logged in to create an album");
      return;
    }

    setIsLoading(true);
    try {
      const slug = generateSlug(values.name);
      
      // Insert album into database
      const { data: albumData, error: albumError } = await supabase
        .from('albums')
        .insert({
          name: values.name,
          description: values.description || null,
          slug,
          is_public: values.isPublic,
          user_id: session.user.id // Add the user_id field
        })
        .select()
        .single();

      if (albumError) {
        throw albumError;
      }

      // Format the album data for the frontend
      const album: Album = {
        id: albumData.id,
        name: albumData.name,
        description: albumData.description || undefined,
        slug: albumData.slug,
        createdAt: new Date(albumData.created_at).getTime(),
        updatedAt: new Date(albumData.updated_at).getTime(),
        isPublic: albumData.is_public,
        views: albumData.views,
        userId: albumData.user_id, // Include userId in the album object
        images: [],
      };

      toast.success("Album created successfully!");
      form.reset();
      
      if (onSuccess) {
        onSuccess(album);
      }
    } catch (error) {
      console.error("Error creating album:", error);
      toast.error("Failed to create album");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-white/10">
      <CardHeader>
        <CardTitle>Create New Album</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Album Name</FormLabel>
                  <FormControl>
                    <Input placeholder="My Collection" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="A collection of my favorite images..."
                      className="resize-none" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isPublic"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 border-white/10">
                  <div className="space-y-0.5">
                    <FormLabel>Public Album</FormLabel>
                    <FormDescription>
                      Make this album visible to everyone
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Album"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AlbumCreator;
